import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FETCH_PRODUCT_WITH_PRICE_URL } from "../../../service/URL";
import { http } from "../../../service/http";
import { useAppSelector } from "../redux-hooks";
import {
    agreementCountForExpiredProductPrice, bulkInactivateProductAgreement, createMassProductAgreement, deleteProductAgreement, fetchProductsByUserRole, fetchProductsIncludingInactiveByUserRole, findAgreementsByPracticeAndProductCategory, generateAgreementsForExpiredProductPrice,
    getProductAgreementList, getProductPricingByMAC, getSKUProductAgreementList, hasAgreementsBasedOnProductType, hasAncilliaryAgreements, inactivateProductAgreement, initiateAgreements, previewOnboardAgreement,
    saveProductAgreement, signManufactureProductAgreement, signPracticeProductAgreement,
    uploadSignedAgreement
} from "../../../service/ProductService";
import { DocumentInfo, PracticeInfo } from "../practiceSlice";
import { InterceptorOption, LoadingType } from "../../../types/CommonTypes";
import { ProductInfo } from "../product/productListSlice";
import { fetchPracticesByUserRole } from "../../../service/PracticeService";


export type MacInfo = {
    macId: number,
    macName: string
}

export type practiceMac = {
    macId: number,
    description: string
}


type CreateAgreement = {
    practiceId: string | null;
    effectiveDate: string;
    MAC: {};

};

export type PracticeProductAgreement = {
    practiceProductId: number,
    productId: number,
    productName: string;
    hasSKUPrice: boolean;
    effectiveDate: string;
    inactiveDate: string;
    agreementStatus: string;
    discountType: string;
    extendedPrice: string;
    practiceName: string;
    providerName: string;
    auditTimestamp: string;
    fileType: string;
    //CS-1628
    canDeleteAgreement?: boolean;
    manufacturerName?: string; //CS-1625
    productCategory?: string;
    macId?: number;
    macName?: string;
    billingType?:string;
    practiceProviderId?: number | null;
    agreementGroupId?: string | null;
}


export type PracticeLocationInfo = {
    locationId: number;
    locationType: string;
    streetAddress: string;
    unitNumber: string;
    city: string;
    state: string;
    zipCode: string;
  }
  
  export type PracticeProviderInfo = {
    providerId: number;
    firstName: string;
    middleName: string;
    lastName: string;
    npi: string;
    ptan: string;
    createdDate: Date;
    inactiveDate: Date;
    title: string;
  }

  export const AGREEMENT_LIST_SORT_FIELDS = [
    "productName",
    "manufacturerName",
    "practiceName",
    "providerName",
    "effectiveDate",
    "inactiveDate",
    "extendedPrice",
    "agreementStatus",
    "auditTimestamp",
  ] as const;

  export type AgreementListSortField = (typeof AGREEMENT_LIST_SORT_FIELDS)[number];

  export type SearchPracticeProductAgreementRequest = {
    agreementIds: number[] | null
    productIds: number[] | null ;
    practiceIds: number[] | null;
    agreementStatus: string[];
    agreementValidity: string[]; //CS-1288
    currentPage: number;
    itemsPerPage: number;
    manufacturerIds?: number[];//CS-1625
    agreementTypes?: string[];
    downloadState?: boolean;
    // Optional. Absent values keep the existing last-updated order.
    sortBy?: AgreementListSortField;
    sortOrder?: "asc" | "desc";
  }

  export type SKUProductAgreementRequest = {
    productId: number;
    practiceProductId: number;
  }

  export type PracticeProductInfo = {
    productId: string;
    practiceProviderId: number | null;
    discountType?: string | number;
    extendedPrice: DoubleRange | number | null;
    upfrontPrice?: number | null; // CS-1845
    upfrontDiscount?: number | null; // CS-1845
    lateFeePercent?: number | null; 
    productPriceId : string | null;
    macId?: string | number | null;
    productItemId?: number,
    customPrice?: number
   };

   export type inactivateInfo = {
    id: number;
    inactiveDate: string;
   };

    export type bulkInactivateInfo = {
    ids: number[]
    inactiveDate: string;
   };

    export type createMassAgreementRequest = {
    practiceProductIds: number[]
   };

    export interface Attachment {
        content: string;
        documentName: string;
        fileType: string;
        type: string
    }

    export type UploadSignedAgreementInfo = {
        practiceProductId: number
        documentName: string,
        content: string,
        type: string,
        fileType: string
    }
    
   interface PaginationAgreementList {
    content: PracticeProductAgreement[];
    totalElements: number;
    totalPages: number;
    size: number;
  }
  

type CreateAgreementState = {
    createAgreement: CreateAgreement;
    macInfo?: MacInfo[] | null;
    productAgreementsList?: PaginationAgreementList | null;
    skuProductAgreementList?: any; 
    document?: DocumentInfo | null;
    status: string;
    error: string | null;
    isValid: boolean;
    fetchProductsByRoleStatusAgreementSlice: LoadingType,
    productsByUserRoleAgreementSlice: ProductInfo[],
    getPracticesByRoleStatusAgreementSlice: LoadingType;
    practiceByUserRoleAgreementSlice?: PracticeInfo[] | null;
    checkAncilliaryAgreementsStatus: LoadingType;
    agreementsByPracticeAndCategory: PracticeProductAgreement[] | null;
    agreementsByPracticeAndCategoryStatus: LoadingType
}
let initialState: CreateAgreementState = {
    createAgreement: {
        practiceId: '',
        effectiveDate: '',
        MAC: {}
    },
    productAgreementsList: undefined,
    skuProductAgreementList: undefined, 
    status: "idle",
    error: null,
    isValid: false,
    fetchProductsByRoleStatusAgreementSlice: "idle",
    productsByUserRoleAgreementSlice: [],
    getPracticesByRoleStatusAgreementSlice: "idle",
    practiceByUserRoleAgreementSlice: [],
    checkAncilliaryAgreementsStatus: 'idle',
    agreementsByPracticeAndCategory: null,
    agreementsByPracticeAndCategoryStatus: 'idle'
    
};

export const fetchProducts = createAsyncThunk("productList", async () => {
    const response = await http.get(FETCH_PRODUCT_WITH_PRICE_URL);
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchProductPricingByMAC = createAsyncThunk("productPricingByMAC", async (macId: number) => {
    const response = await getProductPricingByMAC(macId);
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchProductAgreementList = createAsyncThunk("ProductAgreementList", async (data: SearchPracticeProductAgreementRequest) => {
    const response = await getProductAgreementList(data);
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchSKUProductAgreementList = createAsyncThunk("SKUProductAgreementList", async (data: SKUProductAgreementRequest) => {
    const response = await getSKUProductAgreementList(data);
    const v3Response = response.data;
    return v3Response.data;
});

export const createProductAgreement = createAsyncThunk<
any, {agreementData: any, interceptorOption?: InterceptorOption}
>("createProductAgreement", async (data, { rejectWithValue }) => {
    try{
        const { agreementData, interceptorOption } = data;
        const response = await saveProductAgreement(agreementData, interceptorOption);
        const v3Response = response.data;
        return v3Response;
    } catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);

    }
});

export const practiceSignProductAgreement = createAsyncThunk("practiceSignProductAgreement", async(data: any) => {
    const response = await signPracticeProductAgreement(data);
    const v3Response = response.data;
    return v3Response; 
});

export const manufactureSignProductAgreement = createAsyncThunk("manufactureSignProductAgreement", async(data: any) => {
    const response = await signManufactureProductAgreement(data);
    const v3Response = response.data;
    return v3Response; 
});
//CS-1628
export const deleteProductAgreementById = createAsyncThunk("deleteProductAgreementById", async ({practiceProductId, canDeleteAgreement}: {practiceProductId: number , canDeleteAgreement:boolean}) => {
    const response = await deleteProductAgreement(practiceProductId, canDeleteAgreement);
    const v3Response = response.data;
    return v3Response;  
});

export const inactivateProductAgreementsById = createAsyncThunk<
any, {inactiveData: inactivateInfo, interceptorOption: InterceptorOption}
> 
("inactivateProductAgreementsById", async ( data, { rejectWithValue }) => {
    const { inactiveData, interceptorOption } = data;
    try {
        const response = await inactivateProductAgreement(inactiveData, interceptorOption);
        const v3Response = response.data;
        return v3Response;  
    } catch(error){
        //@ts-ignore
        return rejectWithValue(error?.data)
    }
    
});
export const bulkInactivateProductAgreementByIds = createAsyncThunk<
    any, { inactiveData: bulkInactivateInfo, interceptorOption: InterceptorOption }>
    ("bulkInactivateProductAgreementByIds", async (data, { rejectWithValue }) => {
        const { inactiveData, interceptorOption } = data;
        try {
            const response = await bulkInactivateProductAgreement(inactiveData, interceptorOption);
            const v3Response = response.data;
            return v3Response;
        } catch (error) {
            //@ts-ignore
            return rejectWithValue(error?.data)
        }

    });

export const createMassAgreements = createAsyncThunk<
    any, { ids: createMassAgreementRequest, interceptorOption: InterceptorOption }>
    ("createMassAgreements", async (data, { rejectWithValue }) => {
        const { ids, interceptorOption } = data;
        try {
            const response = await createMassProductAgreement(ids, interceptorOption);
            const v3Response = response.data;
            return v3Response;
        } catch (error) {
            //@ts-ignore
            return rejectWithValue(error?.data)
        }

    });

export const previewAgreement = createAsyncThunk('previewAgreement',async( practiceProductId: string | number ) => {
    const response = await previewOnboardAgreement(practiceProductId);
    const v3Response = response.data;
    return v3Response.data;
  });

export const initiateProductAgreements = createAsyncThunk('initiateProductAgreements', async(data: any[]) => {
    const response = await initiateAgreements(data);
    const v3Response = response.data;
    return v3Response.data;
 });

export const getAgreementCountForExpiredProductPrice = createAsyncThunk('getAgreementCountForExpiredProductPrice', async() => {
    const response = await agreementCountForExpiredProductPrice();
    const v3Response = response.data;
    return v3Response.data;
 });

 export const generateNewAgreementsForExpiredProductPrice = createAsyncThunk('generateNewAgreementsForExpiredProductPrice', async() => {
    const response = await generateAgreementsForExpiredProductPrice();
    const v3Response = response.data;
    return v3Response.data;
 });

 export const fetchProductListByUserRoleAgreementSlice = createAsyncThunk("fetchProductListByUserRoleAgreementSlice", async (includeProductPrice: boolean) => {
    const response = await fetchProductsByUserRole(includeProductPrice);
    const v3Response = response.data
    return v3Response.data;
  });

  export const fetchProductListIncludingInactiveByUserRoleAgreementSlice = createAsyncThunk<any, { includeProductPrice: boolean; executedOnly: boolean, skipLoader?: boolean }>("fetchProductListIncludingInactiveByUserRoleAgreementSlice", 
    async ({ includeProductPrice, executedOnly, skipLoader = false }) => {
    const response = await fetchProductsIncludingInactiveByUserRole(includeProductPrice, executedOnly, { 'x-skip-loader': skipLoader.toString() });
    const v3Response = response.data
    return v3Response.data;
  });
  
  export const getPracticeListByUserRoleAgreementSlice = createAsyncThunk('getPracticeListByUserRoleAgreementSlice', async ( isActivePracticeOnly?: boolean ) => {
    const response = await fetchPracticesByUserRole(isActivePracticeOnly);
    const v3Response = response.data;
    return v3Response.data;
  });

  export const uploadSignedAgreementById = createAsyncThunk("uploadSignedAgreementById", async (data: UploadSignedAgreementInfo) => {
    const response = await uploadSignedAgreement(data);
    const v3Response = response.data;
    return v3Response;  
});

export const checkAncilliaryAgreementsForPractice = createAsyncThunk('checkAncilliaryAgreementsForPractice', async ( practiceId: number ) => {
    const response = await hasAncilliaryAgreements(practiceId);
    const v3Response = response.data;
    return v3Response.data;
  });
export const checkAgreementsForPracticeBasedOnProductType = createAsyncThunk('checkAncilliaryAgreementsForPractice', async ( practiceId: number ) => {
    const response = await hasAgreementsBasedOnProductType(practiceId);
    const v3Response = response.data;
    return v3Response.data;
  });

export const getAgreementsByPracticeAndProductCategory = createAsyncThunk<
    any,
    {
        practiceId: number,
        productCategory: string
    }
>("getAgreementsByPracticeAndProductCategory", async (data, { rejectWithValue }) => {
    const { practiceId, productCategory } = data;
    try {
        const response = await findAgreementsByPracticeAndProductCategory(practiceId, productCategory);
        let resp = response.data;
        return resp.data;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

const createAgreementSlice = createSlice({
    name: "createAgreementSlice",
    initialState,
    reducers: {
        updateCreateAgreementObject : (state: CreateAgreementState, action) => {
            state.createAgreement = action.payload;
        },
        resetProductAgreementsList : (state) =>{
            state.productAgreementsList = null;
            state.fetchProductsByRoleStatusAgreementSlice = "idle";
            state.productsByUserRoleAgreementSlice = [];
            state.getPracticesByRoleStatusAgreementSlice = "idle";
            state.practiceByUserRoleAgreementSlice = [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.status = "success";
                state.isValid = true;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.status = "failed";
                state.isValid = false;
                state.error = action.error.message || "Failed to fetch Products";
            })
            .addCase(fetchProductPricingByMAC.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchProductPricingByMAC.fulfilled, (state, action) => {
                state.status = "success";
                state.isValid = true;
            })
            .addCase(fetchProductPricingByMAC.rejected, (state, action) => {
                state.status = "failed";
                state.isValid = false;
                state.error = action.error.message || " Failed to fetch Products by MAC";
            })
            .addCase(fetchProductAgreementList.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchProductAgreementList.fulfilled, (state, action) => {
                state.status = "success";
                state.isValid = true;
                // state.productAgreementsList = action.payload.searchProductAgreementData;
                state.productAgreementsList = action.payload?.searchProductAgreementData; // CS-1984
            })
            .addCase(fetchProductAgreementList.rejected, (state, action) => {
                state.status = "failed";
                state.isValid = false;
                state.error = action.error.message || " Failed to fetch Products by MAC";
            })
            .addCase(fetchSKUProductAgreementList.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchSKUProductAgreementList.fulfilled, (state, action) => {
                state.status = "success";
                state.isValid = true;
                state.skuProductAgreementList = action.payload;
            })
            .addCase(fetchSKUProductAgreementList.rejected, (state, action) => {
                state.status = "failed";
                state.isValid = false;
                state.error = action.error.message || " Failed to fetch SKU Products Agreement";
            })
            .addCase(createProductAgreement.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(createProductAgreement.fulfilled, (state, action) => {
                state.status = "success";
                state.isValid = true;
            })
            .addCase(createProductAgreement.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to create Product Agreement"
            })
            .addCase(practiceSignProductAgreement.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(practiceSignProductAgreement.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(practiceSignProductAgreement.rejected, (state, action) => {
                state.status = "rejected"
            })
            .addCase(manufactureSignProductAgreement.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(manufactureSignProductAgreement.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(manufactureSignProductAgreement.rejected, (state, action) => {
                state.status = "rejected"
            })
            .addCase(deleteProductAgreementById.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(deleteProductAgreementById.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(deleteProductAgreementById.rejected, (state, action) => {
                state.status = "rejected"
            })
            .addCase(inactivateProductAgreementsById.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(inactivateProductAgreementsById.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(inactivateProductAgreementsById.rejected, (state, action) => {
                state.status = "rejected"
            })
            .addCase(bulkInactivateProductAgreementByIds.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(bulkInactivateProductAgreementByIds.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(bulkInactivateProductAgreementByIds.rejected, (state, action) => {
                state.status = "rejected"
            })
            .addCase(createMassAgreements.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(createMassAgreements.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(createMassAgreements.rejected, (state, action) => {
                state.status = "rejected"
            })
            .addCase(previewAgreement.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(previewAgreement.fulfilled, (state, action) => {
                state.status = "success";
                state.document = action.payload;
                state.error = null;
            })
            .addCase(previewAgreement.rejected, (state, action) => {
                state.status = "rejected"
            })
            .addCase(initiateProductAgreements.pending, (state, action) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(initiateProductAgreements.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(initiateProductAgreements.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.error.message || "Failed to initiate agreement";
            })
            .addCase(getAgreementCountForExpiredProductPrice.pending, (state, action) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(getAgreementCountForExpiredProductPrice.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(getAgreementCountForExpiredProductPrice.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.error.message || "Failed to get count of expired product price agreements";
            })
            .addCase(generateNewAgreementsForExpiredProductPrice.pending, (state, action) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(generateNewAgreementsForExpiredProductPrice.fulfilled, (state, action) => {
                state.status = "success";
                state.error = null;
            })
            .addCase(generateNewAgreementsForExpiredProductPrice.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.error.message || "";
            })
            .addCase(fetchProductListByUserRoleAgreementSlice.pending, (state) => {
                state.fetchProductsByRoleStatusAgreementSlice = "loading";
            })
            .addCase(fetchProductListByUserRoleAgreementSlice.fulfilled, (state, action) => {
                state.fetchProductsByRoleStatusAgreementSlice = "success";
                state.productsByUserRoleAgreementSlice = action.payload;
            })
            .addCase(fetchProductListByUserRoleAgreementSlice.rejected, (state, action) => {
                state.fetchProductsByRoleStatusAgreementSlice = 'error';
            })
            .addCase(fetchProductListIncludingInactiveByUserRoleAgreementSlice.pending, (state) => {
                state.fetchProductsByRoleStatusAgreementSlice = "loading";
            })
            .addCase(fetchProductListIncludingInactiveByUserRoleAgreementSlice.fulfilled, (state, action) => {
                state.fetchProductsByRoleStatusAgreementSlice = "success";
                state.productsByUserRoleAgreementSlice = action.payload;
            })
            .addCase(fetchProductListIncludingInactiveByUserRoleAgreementSlice.rejected, (state, action) => {
                state.fetchProductsByRoleStatusAgreementSlice = 'error';
            })
            .addCase(getPracticeListByUserRoleAgreementSlice.pending, (state, action) => {
                state.getPracticesByRoleStatusAgreementSlice = 'loading';
            })
            .addCase(getPracticeListByUserRoleAgreementSlice.fulfilled, (state, action) => {
                state.getPracticesByRoleStatusAgreementSlice = 'success';
                state.practiceByUserRoleAgreementSlice = action.payload;
            })
            .addCase(getPracticeListByUserRoleAgreementSlice.rejected, (state, action) => {
                state.getPracticesByRoleStatusAgreementSlice = 'error';
            })
            .addCase(uploadSignedAgreementById.pending, (state, action) => {
                state.getPracticesByRoleStatusAgreementSlice = 'loading';
            })
            .addCase(uploadSignedAgreementById.fulfilled, (state, action) => {
                state.getPracticesByRoleStatusAgreementSlice = 'success';
            })
            .addCase(uploadSignedAgreementById.rejected, (state, action) => {
                state.getPracticesByRoleStatusAgreementSlice = 'error';
            })
            .addCase(checkAncilliaryAgreementsForPractice.pending, (state, action) => {
                state.checkAncilliaryAgreementsStatus = 'loading';
            })
            .addCase(checkAncilliaryAgreementsForPractice.fulfilled, (state, action) => {
                state.checkAncilliaryAgreementsStatus = 'success';
            })
            .addCase(checkAncilliaryAgreementsForPractice.rejected, (state, action) => {
                state.checkAncilliaryAgreementsStatus = 'error';
            })
            .addCase(getAgreementsByPracticeAndProductCategory.pending, (state) => {
                state.agreementsByPracticeAndCategoryStatus = "loading";
            })
            .addCase(getAgreementsByPracticeAndProductCategory.fulfilled, (state, action) => {
                state.agreementsByPracticeAndCategoryStatus = "success";
                state.agreementsByPracticeAndCategory= action.payload;
            })
            .addCase(getAgreementsByPracticeAndProductCategory.rejected, (state, action) => {
                state.agreementsByPracticeAndCategoryStatus = 'error';
            })
    }
});

export const useCreateAgreementSlice = () => useAppSelector((state) => state.createAgreementSlice);
export const { updateCreateAgreementObject, resetProductAgreementsList } = createAgreementSlice.actions;
export default createAgreementSlice.reducer;

